// src/utils/offer-pdf.js
// Genera el PDF de una oferta (con condiciones, especiales y generales).
// Requiere: npm i jspdf
import { jsPDF } from "jspdf";

/** Textos por defecto (podés editarlos a gusto) */
const GENERAL_CONDITIONS_DEFAULT = `Se emitirán certificados de calibración conforme a los requisitos de la Norma ISO 17025 / IRAM 301.

NOTA 1: En caso de realizar un ajuste y con el consentimiento escrito del cliente debidamente notificado, el costo del servicio se incrementará un 50% del valor cotizado.

NOTA 2: En caso de solicitar cumplimiento de especificación, se deberá establecer cuál será el criterio de aceptación a utilizar.
De no suceder lo mencionado anteriormente el criterio de aceptación a aplicar será:
|Eadm| ≥ |Es| + |Uexp|
Dónde:
|Eadm| = Error Admisible
|Es| = Error Sistemático obtenido
|Uexp| = Incertidumbre de Medición

Nuestro laboratorio de calibración en el área de presión forma parte del "Servicio Argentino de Calibraciones" (SAC) Agencia Nº 55, ente que el "INTI" (Instituto Nacional de Tecnología Industrial) ha creado para la supervisión de los laboratorios de calibración según los requerimientos establecidos en la norma ISO 17025. Las capacidades de medición y los alcances de nuestro laboratorio pueden ser consultados en la página del INTI www.inti.gob.ar. La ruta de acceso es: una vez que accedan a la página, ingresar en empresa y luego Servicio Argentino de Calibración.

Importante: Una vez finalizado el período de validez de la cotización de reparación (30 días hábiles corridos) y en caso de no recibir acción alguna a tomar con el instrumento, el mismo se pondrá a disponibilidad de CALINS S.A. para proceder a su destrucción y/o descarte sin notificación alguna
Se informará para cada resultado, la incertidumbre combinada expandida y cuando sea posible, una declaración de cumplimiento con la especificación de exactitud.

IMPORTANTE: Adjuntar manuales de operación y servicio, cables de conexión, cargador y pilas (si corresponde).`;

const PURCHASE_NOTES_DEFAULT = `En caso de confirmar la compra, se solicita hacer mención del número de cotización de referencia en la misma.

Estos precios son válidos para el total de los ítems. En caso de alguna variación en las cantidades o especificaciones técnicas, favor consultar.

Las fotos y/o los diagramas de la presente oferta son de carácter ilustrativo. La eventual orden de compra deberá respetar las presentes condiciones comerciales. En caso de modificarse las mismas CALINS S.A. se reserva el derecho de no aceptar la orden. Los modelos de productos ofrecidos en la presente oferta han sido seleccionados en función de los datos de proceso suministrados por ustedes a nuestro departamento de ventas.

Ante la ocurrencia de un evento de fuerza mayor que afecte la capacidad de entregar los instrumentos en tiempo y forma, CALINS S.A.a podrá considerar una extensión en los tiempos de entrega equivalente al tiempo realmente demorado por dicho evento.
Tras la recepción de una orden de compra formal, habrá cargos por cancelación luego de transcurrir 48hs de la recepción de la misma.
Los cargos serán:
2 a 7 días después de la recepción del pedido, 30 % del valor de la OC
2 a 4 semanas después de la recepción del pedido, 50% del valor de la OC
Más de 4 semanas después de la recepción del pedido, 100% del valor de la OC.`;

const USD_NOTE_DEFAULT = `(*) Se cancelará en Pesos, teniendo en cuenta el tipo de cambio vigente al día anterior a la fecha de acreditación del pago, según la cotización del USD/Euro divisa vendedor suministrada por el BNA. En caso de existir una variación mayor al +/-2% entre la relación utilizada para el pago y la que se encuentre vigente en el momento de la acreditación efectiva de los valores, se procederá a realizar el ajuste correspondiente mediante nota de crédito o débito según corresponda.
Asimismo si al momento de la entrega existieran cambios en los impuestos, tasas o un nuevo gravamen, el monto de la oferta será ajustado de acuerdo a estos.
Forma de pago: ver en encabezado.
En caso de mora en el pago CALINS S.A. se reserva el derecho de emitir una Nota de Débito aplicando una tasa efectiva mensual de 3,5%. El pago será considerado efectivo a partir de la acreditación de los valores.
Plazo de entrega: Para todos los casos, luego de la recepción y aceptación de la orden de compra y de la acreditación del anticipo en caso de corresponder.
Para el caso de instrumentos o materiales que deban ser importados, los plazos de entrega se verán sujetos a modificaciones según resoluciones de la Aduana Argentina.
Lugar de entrega: Nuestras oficinas ubicadas en: Carril Rodríguez Peña 2065 - Maipú - Mendoza, o entregaremos en el expreso de Mendoza que nos indiquen.`;

/** Utilidades */
const mm = (v) => v * 2.83465;

const money = (n, symbol) =>
  `${symbol}${Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// ✅ Robusto: si el valor no es una fecha válida, devuelve el texto tal cual
const fmtDate = (value) => {
  const v = value == null ? "" : String(value).trim();
  if (!v) return "";

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;

  return d.toLocaleDateString("es-AR");
};

// --- INICIO NUEVAS FUNCIONES DE LIMPIEZA ---
function decodeHtmlEntities(input) {
  const s = String(input ?? "");

  // Decodificación rápida de entidades comunes
  return s
    .replaceAll("&nbsp;", " ")
    .replaceAll("&amp;", "&")
    .replaceAll("&quot;", '"')
    .replaceAll("&#39;", "'")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&le;", "≤")
    .replaceAll("&ge;", "≥")
    .replaceAll("&plusmn;", "±");
}

function normalizePdfText(input) {
  // 1. Decodifica HTML
  // 2. Normaliza saltos de línea y espacios
  // 3. Reemplaza símbolos problemáticos (≥, ≤) por versión ASCII compatible con fuentes estándar de PDF
  return decodeHtmlEntities(input)
    .replace(/\r\n/g, "\n")
    .replace(/\u00A0/g, " ") // NBSP a espacio normal
    .replace(/\t/g, " ") // \u2705 TAB -> espacio: jsPDF mide mal los tabs y la descripci\u00F3n se desbordaba sobre la columna de precios
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width characters
    .replaceAll("≥", ">=")
    .replaceAll("≤", "<=")
    // --- FIX: símbolos fuera de Latin-1 que rompen jsPDF (UTF-16 -> "&P&i&n&z&a&") ---
    .replace(/[ΩΩω]/g, "ohm") // Omega / signo ohm -> "ohm"
    .replace(/μ/g, "u") // mu griega -> u (el micro µ 0xB5 queda igual)
    .replace(/[‒-―]/g, "-") // guiones largos – —  ->  -
    .replace(/[‘’‛]/g, "'") // comillas simples tipográficas
    .replace(/[“”]/g, '"') // comillas dobles tipográficas
    .replace(/…/g, "...") // elipsis …
    .replace(/[^\u0000-\u00FF]/g, ""); // RED DE SEGURIDAD: quita todo lo no-Latin-1
}
// --- FIN NUEVAS FUNCIONES DE LIMPIEZA ---

function split(doc, text, widthPt) {
  return doc.splitTextToSize(text, widthPt);
}

// ✅ Separa dirección en calle / localidad / provincia desde un solo campo
// Fallback para clientes viejos donde todo estaba en `address`.
function parseAddressPartsFromSingleField(address) {
  const raw = String(address ?? "").trim();
  let street = "";
  let locality = "";
  let province = "";

  if (raw) {
    const norm = raw.replace(/\s*–\s*/g, "-").replace(/\s*—\s*/g, "-");
    const parts = norm
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    street = parts[0] || "";
    const rest = parts.slice(1).join(", ").trim();

    if (rest) {
      const byDash = rest
        .split("-")
        .map((s) => s.trim())
        .filter(Boolean);
      const byComma = rest
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (byDash.length >= 2) {
        locality = byDash[0] || "";
        province = byDash[1] || "";
      } else if (byComma.length >= 2) {
        locality = byComma[0] || "";
        province = byComma[1] || "";
      } else {
        locality = rest;
      }
    }
  }

  street = street.replace(/\s+/g, " ").trim();
  locality = locality.replace(/\s+/g, " ").trim();
  province = province.replace(/\s+/g, " ").trim();

  return { street, locality, province };
}

function calcLineDiscountAmount(item = {}) {
  const base = Number(item.qty || 0) * Number(item.price || 0);
  const value = Math.max(0, Number(item.discount) || 0);
  const type = item.discount_type || "percentage";

  if (type === "fixed") return Math.min(base, value);
  return Math.min(base, base * (value / 100));
}

function calcTotals(services = [], ivaRate = 0.21) {
  const grossSubtotal = services.reduce(
    (s, it) => s + Number(it.qty || 0) * Number(it.price || 0),
    0
  );
  const lineDiscountTotal = services.reduce(
    (s, it) => s + calcLineDiscountAmount(it),
    0
  );
  const subtotal = Math.max(0, grossSubtotal - lineDiscountTotal);
  const tax = +(subtotal * (Number(ivaRate) || 0)).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);
  return { subtotal, tax, total, grossSubtotal, lineDiscountTotal };
}

function calcDiscountAmount(offer = {}, subtotal = 0) {
  const discountValue = Math.max(0, Number(offer?.discount) || 0);
  const discountType = String(offer?.discount_type || "percentage");

  if (discountType === "fixed") {
    return Math.min(subtotal, discountValue);
  }

  return Math.min(subtotal, subtotal * (discountValue / 100));
}

// Quita prefijo CT- SOLO para la columna "Ref." (en la tabla)
function stripRefPrefix(ref) {
  const r = String(ref || "").trim();
  if (!r) return "—";
  return r.startsWith("CT-") ? r.slice(3) : r;
}

// Trunca con "…" para que entre en un ancho máximo (en puntos)
function fitText(doc, text, maxWidthPt) {
  const t = String(text ?? "");
  if (!t) return "";
  if (doc.getTextWidth(t) <= maxWidthPt) return t;

  const ell = "…";
  let out = t;
  while (out.length > 1 && doc.getTextWidth(out + ell) > maxWidthPt) {
    out = out.slice(0, -1);
  }
  return out.length ? out + ell : ell;
}

// ✅ Validez como texto (default 15 días)
function getValidityText(offer = {}) {
  const v = String(offer?.validity_date ?? "").trim();
  return v || "15 días";
}

/**
 * Genera el PDF de una oferta.
 * @param {object} offer  Oferta completa (cliente, services, condiciones, especiales y generales).
 * @param {object} lab    Config del laboratorio: { name, cuit, address, city, country, phone, email, logoBase64, logoUrl }
 * @returns {Promise<Blob>}
 */
export async function generateOfferPDF(offer, lab = {}) {
  const {
    code,
    created_at,
    client = {},
    services = [],
    notes = "",
    validity_date, // (texto)
    delivery_place,
    delivery_time,
    payment_method,
    price_currency,
    iva_rate = 21,
    special_conditions,
    pdf_overrides = {},
    subtotal: st,
    tax: tx,
    total: tt,

    // ✅ NUEVO: contacto “con quien se negocia” guardado en la OFERTA
    contact_name,
    contact_email,
  } = offer || {};

  const doc = new jsPDF({ unit: "pt", format: "A4", compress: true });

  // Derivados
  const isUSD = String(price_currency || "").toUpperCase().includes("USD");
  const symbol = isUSD ? "US$" : "$";
  const ivaRateDecimal =
    Number(iva_rate) > 1 ? Number(iva_rate) / 100 : Number(iva_rate) || 0.21;

  // Totales base para mostrar el descuento sin duplicarlo
  const totalsFromServices = calcTotals(services, ivaRateDecimal);
  const totals =
    st != null && tx != null && tt != null
      ? { subtotal: st, tax: tx, total: tt }
      : totalsFromServices;

  const grossSubtotalBase = totalsFromServices.grossSubtotal || totalsFromServices.subtotal || totals.subtotal || 0;
  const lineDiscountTotal = totalsFromServices.lineDiscountTotal || 0;
  const subtotalBase = totalsFromServices.subtotal || totals.subtotal || 0;
  const discountAmount = calcDiscountAmount(offer, subtotalBase);
  const subtotalAfterDiscount = Math.max(0, subtotalBase - discountAmount);
  const taxAfterDiscount = +(subtotalAfterDiscount * ivaRateDecimal).toFixed(2);
  const totalAfterDiscount = +(subtotalAfterDiscount + taxAfterDiscount).toFixed(2);

  // Overrides efectivos
  const ovr = pdf_overrides || {};
  const title = ovr.title ?? "COTIZACIÓN";

  // ✅ Validez como texto (15 días por defecto)
  const validityText = String(validity_date ?? "").trim() || "15 días";

  const discountType = String(offer?.discount_type || "percentage");
  const discountLine =
    discountAmount > 0
      ? `Descuento aplicado: ${
          discountType === "fixed"
            ? money(discountAmount, symbol)
            : `${Number(offer?.discount || 0)}% (${money(discountAmount, symbol)})`
        }`
      : null;

  const autoCommercialText = [
    `Validez de la oferta: ${validityText}`,
    delivery_place ? `Lugar de entrega: ${delivery_place}` : null,
    delivery_time ? `Plazo de entrega: ${delivery_time}` : null,
    payment_method ? `Forma de pago: ${payment_method}` : null,
    discountLine,
    `IVA: ${Number(iva_rate)}%`,
    `Precio ref. / Moneda: ${price_currency || (isUSD ? "USD" : "ARS")}`,
  ]
    .filter(Boolean)
    .join("\n");

  const textCommercial = ovr.commercial ?? autoCommercialText;
  const textSpecial = ovr.special ?? (special_conditions || "");
  const textGeneral = ovr.general ?? GENERAL_CONDITIONS_DEFAULT;
  const textNotes = ovr.notes ?? PURCHASE_NOTES_DEFAULT;

  // Cargar logo si viene URL
  let logoDataUrl = lab.logoBase64;
  if (!logoDataUrl && lab.logoUrl) {
    try {
      const res = await fetch(lab.logoUrl, { mode: "cors" });
      const blob = await res.blob();
      logoDataUrl = await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => resolve(null);
        fr.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn("No se pudo cargar el logo:", e);
    }
  }

  // Mantener exactamente las mismas proporciones del logo en todas las páginas.
  const offerLogoWidth = mm(52);
  const offerLogoHeight = mm(16);
  const offerLogoTop = mm(10);

  /** Header completo (solo página 1) */
  const drawFullHeader = () => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoX = pageWidth - mm(12) - offerLogoWidth;

    // Logo Calins (arriba derecha)
    if (logoDataUrl) {
      try {
        doc.addImage(
          logoDataUrl,
          "PNG",
          logoX,
          offerLogoTop,
          offerLogoWidth,
          offerLogoHeight
        );
      } catch (e) {
        console.warn("Error al agregar logo:", e);
      }
    }

    // Título
    doc.setFont("helvetica", "bold");
    doc.setFontSize(21);
    doc.text(title, mm(105), mm(22), { align: "center" });

    // COLUMNA IZQUIERDA: CLIENTE
    let leftY = mm(35);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(client.name || "—", mm(12), leftY);

    leftY += 11;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    // ✅ CAMBIO: contacto que se imprime en el PDF
    // Prioridad: contacto de la OFERTA (negociación)
    // Fallback: contacto de referencia del CLIENTE
    const pdfContactName =
      String(contact_name ?? "").trim() || String(client.contact ?? "").trim();
    const pdfContactEmail =
      String(contact_email ?? "").trim() || String(client.email ?? "").trim();

    if (pdfContactName) {
      doc.text(pdfContactName, mm(12), leftY);
      leftY += 10;
    }
    if (pdfContactEmail) {
      doc.text(pdfContactEmail, mm(12), leftY);
      leftY += 10;
    }

    // ✅ Dirección / Localidad / Provincia
    // - address: calle y número
    // - locality/province: nuevas columnas
    // - fallback: si vienen vacíos, intentamos extraer desde address viejo
    {
      const parsed = parseAddressPartsFromSingleField(client.address);
      const street = String(client.address ?? parsed.street ?? "").trim();
      const locality = String(client.locality ?? parsed.locality ?? "").trim();
      const province = String(client.province ?? parsed.province ?? "").trim();

      const maxW = mm(80);

      const dirLines = split(doc, `Dirección: ${street || ""}`.trim(), maxW);
      dirLines.forEach((line) => {
        doc.text(line, mm(12), leftY);
        leftY += 10;
      });

      const locLines = split(doc, `Localidad: ${locality || ""}`.trim(), maxW);
      locLines.forEach((line) => {
        doc.text(line, mm(12), leftY);
        leftY += 10;
      });

      const provLines = split(doc, `Provincia: ${province || ""}`.trim(), maxW);
      provLines.forEach((line) => {
        doc.text(line, mm(12), leftY);
        leftY += 10;
      });
    }

    // CUIT
    const clientCuit = client.cuit || client.tax_id;
    if (clientCuit) {
      doc.text(`CUIT: ${clientCuit}`, mm(12), leftY);
      leftY += 10;
    }

    // Nº Cliente
    if (client.client_number) {
      doc.text(`Nº Cliente: ${client.client_number}`, mm(12), leftY);
      leftY += 10;
    }

    // COLUMNA DERECHA: DATOS
    let rightY = mm(38);
    const rightX = mm(198);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(`Nº: ${code || "—"}`, rightX, rightY, { align: "right" });

    rightY += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    doc.text(`Fecha: ${fmtDate(created_at)}`, rightX, rightY, { align: "right" });

    rightY += 10;
    doc.text(`Teléfono: ${lab.phone || "+54 261 5322685"}`, rightX, rightY, {
      align: "right",
    });
    rightY += 10;
    doc.text(`Responsable: Daniel Kemeñy`, rightX, rightY, { align: "right" });
    rightY += 10;
    doc.text(`E-Mail: ${lab.email || "ventas@calins.com.ar"}`, rightX, rightY, {
      align: "right",
    });

    // ✅ Referencia/Observaciones: NO truncar, envolver en múltiples renglones
    if (notes && String(notes).trim()) {
      rightY += 10;
      const maxW = mm(90);
      const prefix = "Referencia: ";
      const noteNorm = String(notes).replace(/\r\n/g, "\n").trim();

      // dividimos solo el cuerpo y luego anteponemos el prefijo en la primera línea
      const bodyLines = split(doc, noteNorm, maxW);
      const lines = bodyLines.map((l, idx) => (idx === 0 ? prefix + l : l));

      lines.forEach((line) => {
        doc.text(line, rightX, rightY, { align: "right" });
        rightY += 10;
      });
    }

    // Línea separadora
    doc.setDrawColor(200);
    doc.line(mm(12), mm(70), mm(198), mm(70));

    return mm(75);
  };

  /** Header simple (páginas 2+) */
  const drawSimpleHeader = () => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoX = pageWidth - mm(12) - offerLogoWidth;

    if (logoDataUrl) {
      try {
        doc.addImage(
          logoDataUrl,
          "PNG",
          logoX,
          offerLogoTop,
          offerLogoWidth,
          offerLogoHeight
        );
      } catch (e) {
        console.warn("Error al agregar logo:", e);
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text(title, mm(105), mm(20), { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Nº: ${code || "—"}`, mm(105), mm(28), { align: "center" });

    doc.setDrawColor(200);
    doc.line(mm(12), mm(35), mm(198), mm(35));

    return mm(40);
  };

  /** Pie de página */
  const drawFooter = (pageNum, totalPages) => {
    const yBase = mm(287);

    doc.setTextColor(80);
    doc.setDrawColor(200);

    // línea separadora arriba del footer
    doc.line(mm(12), yBase - mm(3), mm(198), yBase - mm(3));

    // ✅ Definimos 2 renglones comunes para las 3 columnas
    const yTop = yBase - mm(3); // renglón superior
    const yBot = yBase; // renglón inferior

    // --- IZQUIERDA: Dirección (2 líneas alineadas con las otras columnas) ---
    const address =
      lab.address || "Carril Rodríguez Peña 2065. CP 5511,Maipu-Mendoza-Argentina";

    // Partimos en 2 líneas de forma simple y segura
    // (primero intentamos por ". " como estabas haciendo)
    const addressParts = String(address).split(". ");
    let addrLine1 = addressParts[0] ? addressParts[0].trim() : "";
    let addrLine2 = addressParts[1] ? addressParts[1].trim() : "";

    if (addrLine1 && !addrLine1.endsWith(".")) addrLine1 += ".";
    if (!addrLine1) addrLine1 = "Carril Rodríguez Peña 2065.";
    if (!addrLine2) addrLine2 = "CP 5511, Maipu-Mendoza-Argentina";

    // Ajuste de ancho para que no invada el centro
    const leftMaxW = mm(80); // ancho máximo de la columna izquierda
    addrLine1 = fitText(doc, addrLine1, leftMaxW);
    addrLine2 = fitText(doc, addrLine2, leftMaxW);

    doc.setFontSize(8);
    doc.text(addrLine1, mm(12), yTop);
    doc.text(addrLine2, mm(12), yBot);

    // --- CENTRO: Contacto (arriba) + Página (abajo) ---
    const contact = `Tel: ${
      lab.phone || "+54 0261 4979111"
    } • Email: ${lab.email || "info@calins.com.ar"}`;

    doc.setFontSize(8);
    doc.text(contact, mm(105), yTop, { align: "center" });

    doc.setFontSize(9);
    doc.text(`Pág. ${pageNum} de ${totalPages}`, mm(105), yBot, {
      align: "center",
    });

    // --- DERECHA: IVA (arriba) + CUIT (abajo) ---
    doc.setFontSize(8);
    doc.text("IVA: Responsable Inscripto", mm(198), yTop, { align: "right" });

    doc.setFontSize(9);
    doc.text(`C.U.I.T.: ${lab.cuit || "30-71414789-3"}`, mm(198), yBot, {
      align: "right",
    });

    doc.setTextColor(0);
  };

  // ============ PÁGINA 1 ============
  let y = drawFullHeader();

  // Mensaje
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("De acuerdo a lo solicitado informamos a Uds. Nuestra cotización.", mm(12), y);
  y += 11;
  doc.text("Sin otro particular saludamos atentamente", mm(12), y);
  y += 15;
  doc.setFont("helvetica", "normal");
  doc.text("Sr. Daniel Kemeñy", mm(12), y);
  y += 12;
  doc.text("Director Técnico CALINS S.A.", mm(12), y);
  y += 35;

  // ============ TABLA DE SERVICIOS ============
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);

  // --- Layout de columnas (EN mm) ---
  const tableX = 12;
  const tableW = 186; // 198 - 12
  const pad = 1.5; // padding interno

  const colRefW = 18;
  const colOrdW = 10;
  const colQtyW = 12;
  const colUnitW = 24;
  const colTotalW = 22;

  const colDescW = tableW - (colRefW + colOrdW + colQtyW + colUnitW + colTotalW);

  const xRef = tableX;
  const xOrd = xRef + colRefW;
  const xQty = xOrd + colOrdW;
  const xDesc = xQty + colQtyW;
  const xUnit = xDesc + colDescW;
  const xTotal = xUnit + colUnitW;

  const unitRight = mm(xUnit + colUnitW - pad);
  const totalRight = mm(tableX + tableW);

  // Encabezado de tabla
  doc.setFillColor(245, 245, 245);
  doc.rect(mm(tableX), y - 7, mm(tableW), mm(7), "F");

  doc.setFontSize(9);
  doc.text("Ref.", mm(xRef + pad), y);
  doc.text("Ord.", mm(xOrd + pad), y);
  doc.text("Cant", mm(xQty + pad), y);
  doc.text("Descripción del Servicio", mm(xDesc + pad), y);
  doc.text("Precio Unitario", unitRight, y, { align: "right" });
  doc.text("Total", totalRight, y, { align: "right" });

  y += 11;
  doc.setFont("helvetica", "normal");

  const sortedServices = (services || [])
    .slice()
    .sort((a, b) => (a.position ?? a.order ?? 0) - (b.position ?? b.order ?? 0));

  sortedServices.forEach((svc, idx) => {
    // Ref y Ord
    const refRaw = code || "—";
    const ref = stripRefPrefix(refRaw); // ✅ quita "CT-" para que no se pise con Ord
    const ord = (idx + 1).toString().padStart(2, "0");

    const desc = normalizePdfText(svc.description || "—"); // ✅ normaliza Ω y demás (fix &P&i&n&z&a&)
    const qty = Number(svc.qty || 1);
    const price = Number(svc.price || 0);
    const grossLineSubtotal = qty * price;
    const itemDiscount = calcLineDiscountAmount(svc);
    const subtotal = Math.max(0, grossLineSubtotal - itemDiscount);

    // ✅ Split por el ANCHO REAL de la columna descripción
    const itemDiscountText =
      itemDiscount > 0
        ? `Desc. item: ${
            (svc.discount_type || "percentage") === "fixed"
              ? money(itemDiscount, symbol)
              : `${Number(svc.discount || 0)}% (${money(itemDiscount, symbol)})`
          }`
        : null;
    const descLines = split(
      doc,
      itemDiscountText ? `${desc}\n${itemDiscountText}` : desc,
      mm(colDescW - pad * 2)
    );
    const lineHeight = 11; // pt
    const rowHeight = Math.max(12, descLines.length * lineHeight);

    // Si no cabe, nueva página
    if (y + rowHeight > mm(260)) {
      doc.addPage();
      y = drawSimpleHeader();
      y += 5;
    }

    doc.setFontSize(9);

    // ✅ Ref dentro de su columna (con truncado si hiciera falta)
    const refMaxW = mm(colRefW - pad * 2);
    const refPrinted = fitText(doc, ref, refMaxW);
    doc.text(refPrinted, mm(xRef + pad), y);

    // Ord y Cant
    doc.text(ord, mm(xOrd + pad), y);
    doc.text(String(qty), mm(xQty + pad), y);

    // Descripción (siempre queda dentro de su columna)
    descLines.forEach((ln, i) => {
      const safeLn = fitText(doc, ln, mm(colDescW - pad * 2));
      doc.text(safeLn, mm(xDesc + pad), y + i * lineHeight);
    });

    // Precio y Total (alineados a la derecha)
    doc.text(money(price, symbol), unitRight, y, { align: "right" });
    doc.text(money(subtotal, symbol), totalRight, y, { align: "right" });

    y += rowHeight + 4;
  });

  // Totales
  y += 10;
  if (y > mm(240)) {
    doc.addPage();
    y = drawSimpleHeader() + 10;
  }

  doc.setDrawColor(180);
  doc.line(mm(130), y, mm(198), y);
  y += 11;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Subtotal bruto:", mm(145), y);
  doc.setFont("helvetica", "normal");
  doc.text(money(grossSubtotalBase, symbol), mm(196), y, { align: "right" });

  if (lineDiscountTotal > 0) {
    y += 13;
    doc.setFont("helvetica", "bold");
    doc.text("Desc. ítems:", mm(145), y);
    doc.setFont("helvetica", "normal");
    doc.text(`- ${money(lineDiscountTotal, symbol)}`, mm(196), y, { align: "right" });

    y += 13;
    doc.setFont("helvetica", "bold");
    doc.text("Subtotal:", mm(145), y);
    doc.setFont("helvetica", "normal");
    doc.text(money(subtotalBase, symbol), mm(196), y, { align: "right" });
  }

  if (discountAmount > 0) {
    y += 13;
    doc.setFont("helvetica", "bold");
    doc.text(
      `Descuento${discountType === "fixed" ? "" : ` (${Number(offer?.discount || 0)}%)`}:`,
      mm(145),
      y
    );
    doc.setFont("helvetica", "normal");
    doc.text(`- ${money(discountAmount, symbol)}`, mm(196), y, { align: "right" });
  }

  y += 13;
  doc.setFont("helvetica", "bold");
  doc.text(`IVA (${Number(iva_rate)}%):`, mm(145), y);
  doc.setFont("helvetica", "normal");
  doc.text(money(discountAmount > 0 ? taxAfterDiscount : totals.tax, symbol), mm(196), y, { align: "right" });

  y += 13;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TOTAL:", mm(145), y);
  doc.text(money(discountAmount > 0 ? totalAfterDiscount : totals.total, symbol), mm(196), y, { align: "right" });

  // Metodología empleada
  y += 30;
  if (y > mm(260)) {
    doc.addPage();
    y = drawSimpleHeader() + 5;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Metodología empleada:", mm(12), y);
  doc.setFont("helvetica", "normal");
  doc.text("Procedimiento de calibración por comparación bajo instrumento patrón.", mm(60), y);

  y += 10;

  // ============ CONDICIONES COMERCIALES ============
  y += 20;
  if (y > mm(200)) {
    doc.addPage();
    y = drawSimpleHeader() + 5;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("CONDICIONES COMERCIALES", mm(12), y);
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  // Nota USD (APLICADO NORMALIZACIÓN)
  if (isUSD) {
    const usdLines = split(doc, normalizePdfText(USD_NOTE_DEFAULT), mm(186));
    usdLines.forEach((ln) => {
      if (y > mm(275)) {
        doc.addPage();
        y = drawSimpleHeader() + 5;
      }
      doc.text(ln, mm(12), y);
      y += 12;
    });
    y += 4;
  }

  // Texto comercial (APLICADO NORMALIZACIÓN)
  const commLines = split(doc, normalizePdfText(textCommercial), mm(186));
  commLines.forEach((ln) => {
    if (y > mm(275)) {
      doc.addPage();
      y = drawSimpleHeader() + 5;
    }
    doc.text(ln, mm(12), y);
    y += 12;
  });
  y += 4;

  // Condiciones especiales (APLICADO NORMALIZACIÓN)
  if ((textSpecial || "").trim()) {
    if (y > mm(250)) {
      doc.addPage();
      y = drawSimpleHeader() + 5;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Condiciones comerciales especiales:", mm(12), y);
    y += 11;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const specialLines = split(doc, normalizePdfText(textSpecial), mm(186));
    specialLines.forEach((ln) => {
      if (y > mm(275)) {
        doc.addPage();
        y = drawSimpleHeader() + 5;
      }
      doc.text(ln, mm(12), y);
      y += 12;
    });
    y += 8;
  }

  // ============ CONDICIONES GENERALES ============
  if (y > mm(210)) {
    doc.addPage();
    y = drawSimpleHeader() + 5;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("CONDICIONES GENERALES", mm(12), y);
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  // APLICADO NORMALIZACIÓN AQUI PARA ARREGLAR SÍMBOLOS ≥ ≤
  const genLines = split(doc, normalizePdfText(textGeneral), mm(186));
  genLines.forEach((ln) => {
    if (y > mm(275)) {
      doc.addPage();
      y = drawSimpleHeader() + 5;
    }
    doc.text(ln, mm(12), y);
    y += 12;
  });

  y += 8;

  // ============ NOTAS PARTICULARES ============
  if (y > mm(220)) {
    doc.addPage();
    y = drawSimpleHeader() + 5;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("NOTAS PARTICULARES", mm(12), y);
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  // APLICADO NORMALIZACIÓN
  const purchaseLines = split(doc, normalizePdfText(textNotes), mm(186));
  purchaseLines.forEach((ln) => {
    if (y > mm(275)) {
      doc.addPage();
      y = drawSimpleHeader() + 5;
    }
    doc.text(ln, mm(12), y);
    y += 12;
  });

  // ============ FOOTER ============
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawFooter(p, totalPages);
  }

  return doc.output("blob");
}

/**
 * (Opcional) Helper para descargar directo desde UI
 */
export function downloadOfferPDF(blob, filename = "Oferta.pdf") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ===================== Helpers para el modal ===================== */

/** Construye el texto automático de "Condiciones comerciales" a partir de la oferta */
export function buildCommercialTextFrom(offer = {}) {
  const {
    delivery_place,
    delivery_time,
    payment_method,
    price_currency,
    iva_rate,
    discount,
    discount_type,
  } = offer || {};

  const parts = [];
  parts.push(`Validez de la oferta: ${getValidityText(offer)}`);
  if (delivery_place) parts.push(`Lugar de entrega: ${delivery_place}`);
  if (delivery_time) parts.push(`Plazo de entrega: ${delivery_time}`);
  if (payment_method) parts.push(`Forma de pago: ${payment_method}`);
  const discountValue = Math.max(0, Number(discount) || 0);
  if (discountValue > 0) {
    const kind = String(discount_type || "percentage");
    parts.push(
      `Descuento aplicado: ${
        kind === "fixed"
          ? `${String(price_currency || "ARS").toUpperCase().includes("USD") ? "US$" : "$"}${discountValue.toFixed(2)}`
          : `${discountValue}%`
      }`
    );
  }
  parts.push(`IVA: ${Number(iva_rate ?? 21)}%`);
  parts.push(`Precio ref. / Moneda: ${price_currency || "ARS"}`);
  return parts.join("\n");
}

/** Texto por defecto de "Condiciones generales" */
export function defaultGeneralConditions() {
  return GENERAL_CONDITIONS_DEFAULT;
}

/** Texto por defecto de "Notas / Condiciones de compra" */
export function defaultPurchaseNotes() {
  return PURCHASE_NOTES_DEFAULT;
}
